import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { adminAPI, usersAPI, peoplePlatformAPI, rolesAPI, membersAPI, filesPlatformAPI, positionsAPI } from '@/services';
import {
  isArrearsCritical,
  CONTRIBUTION_ARREARS_CRITICAL_MONTHS,
  harmonizedMemberUnpaidMonthsDisplay,
} from '../../utils/contributionArrearsUi';
import { buildArrearsInvoiceContributionRows, listUnpaidRegularMonthKeys } from '../../utils/contributionArrearsInvoice';
import { searchFieldsMatch } from '../../utils/searchText';
import { buildMemberDashboardPersonalStats } from '../../utils/memberDashboardPersonalStats';
import { useToast } from '../../components/Toast';
import { useConfirmDialog } from '../../components/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useSettings } from '../../contexts/SettingsContext';
import SearchAndFilterBar from '../../components/SearchAndFilterBar';
import Pagination from '../../components/Pagination';
import PageHeader from '../../components/PageHeader';
import TableListCheckbox from '../../components/TableListCheckbox';
import TableRowActions from '../../components/TableRowActions';
import ContributionInvoiceModal from '../../components/ContributionInvoiceModal';
import DocumentFileDropZone from '../../components/DocumentFileDropZone';
import SupplementaryDocumentsUploader from '../../components/SupplementaryDocumentsUploader';
import { memberController } from '../../controllers/memberController';
import {
  buildPeopleMemberPayload,
  getSupplementaryDocumentIdsFromMemberRow,
  normalizeContributionsPayload,
} from '../../models/memberModel';
import { isOrgAdminRole } from '../../utils/iamRoles';
import { parseTabularFile } from '../../utils/tabularImport';
import {
  PROFILE_TYPES,
  SEX_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  IDENTITY_DOC_TYPES,
  LANGUAGE_OPTIONS,
  NATIONALITY_OPTIONS,
  NATIONALITY_CODE_SET,
} from '../../utils/peopleFormOptions';
import { downloadMembersImportTemplate } from '../../utils/importTemplates';
import ModalShell from '../../components/ModalShell';
import UiSurface from '../../components/UiSurface';
import { staffMembersBasePath } from '../../utils/portalAdminNav';
import { useIsMobileSurface } from '../../hooks/useUiSurfaceVisible';
import { getResolvedPortalTitle } from '../../utils/portalBranding';

/** Libellé mois long + année à partir de YYYY-MM (liste membres, aligné tableau de bord). */
function formatLongMonthYearCapitalized(ym, localeFmt) {
  const s = String(ym || '').trim();
  if (!/^\d{4}-\d{2}$/.test(s)) return null;
  try {
    const raw = new Date(`${s}-01T12:00:00`).toLocaleDateString(localeFmt, { month: 'long', year: 'numeric' });
    if (!raw) return null;
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  } catch {
    return null;
  }
}

/** Mois impayés pour la liste membres (même logique que fiche / tableau de bord). */
function unpaidMonthsDisplayFromStats(stats) {
  if (!stats || typeof stats !== 'object') return 0;
  return Number(stats.unpaid_months_harmonized ?? stats.unpaid_months ?? 0);
}

const buildEmptyMemberFormData = (defaultType = 'member') => ({
  name: '',
  email: '',
  phone: '',
  join_date: '',
  status: 'active',
  commission: '',
  type: defaultType,
  first_name: '',
  last_name: '',
  middle_name: '',
  date_of_birth: '',
  place_of_birth: '',
  sex: '',
  marital_status: '',
  nationality: '',
  profession: '',
  id_number: '',
  identity_document_type: '',
  identity_document_number: '',
  identity_document_expiration_date: '',
  identity_document_file_id: '',
  photo_file_id: '',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_country: '',
  address_postal_code: '',
  preferred_language: '',
  member_number: '',
});

const Members = () => {
  const { t, i18n } = useTranslation('common');
  const localeFmt = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const isMobileSurface = useIsMobileSurface();
  const [projectPositions, setProjectPositions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await positionsAPI.getList();
        if (!cancelled) setProjectPositions(res.data?.positions || []);
      } catch {
        if (!cancelled) setProjectPositions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const positionLabelById = useMemo(() => {
    const m = new Map();
    for (const p of projectPositions || []) {
      const id = String(p?.id || '').trim();
      if (!id) continue;
      m.set(id, String(p?.label || p?.key || id));
    }
    return m;
  }, [projectPositions]);

  /** Libellé position projet (utilisateur lié), pas le rôle IAM `participant`. */
  const memberProjectPositionLabel = (member) => {
    const pid = String(member?.position_id || '').trim();
    if (!pid) return '—';
    return positionLabelById.get(pid) || pid;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const membersBasePath = staffMembersBasePath(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentProjectId = localStorage.getItem('project_id') || null;
  const { settings } = useSettings();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const { hasPermission, role } = usePermissions();
  const canHardDeleteMember = isOrgAdminRole(role);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoadingMemberId, setInvoiceLoadingMemberId] = useState('');
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCommission, setSelectedCommission] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('members'); // 'members' or 'commissions'
  const [showForm, setShowForm] = useState(false);
  const [showCommissionForm, setShowCommissionForm] = useState(false);
  const [selectedCommissionItem, setSelectedCommissionItem] = useState(null);

  const getFriendlyContactConflictMessage = (err) => {
    if (Number(err?.response?.status) !== 409) return null;
    return 'Un enregistrement existe déjà avec cet email ou ce numéro de téléphone.';
  };
  const formatMemberDisplayName = (member) => {
    const last = String(member?.last_name || '').trim();
    const first = String(member?.first_name || '').trim();
    if (last && first) return `${last}, ${first}`;
    return String(member?.name || '').trim() || 'Sans nom';
  };

  const formatInvoiceMonthLabel = (month) => {
    const label = formatLongMonthYearCapitalized(month, localeFmt);
    return label || String(month || '—');
  };

  const formatInvoiceDueDate = (monthKey) => {
    const billing = settings?.financial?.contribution_billing || {};
    const day = Math.min(28, Math.max(1, Number(billing.payment_deadline_day) || 15));
    const ym = String(monthKey || '').trim().slice(0, 7);
    if (/^\d{4}-\d{2}$/.test(ym)) {
      const [y, m] = ym.split('-').map(Number);
      // Échéance = jour limite du mois suivant.
      return new Date(y, m, day).toLocaleDateString(localeFmt, { dateStyle: 'medium' });
    }
    const now = new Date();
    const due = new Date(now.getFullYear(), now.getMonth(), day);
    if (due.getTime() < now.getTime()) due.setMonth(due.getMonth() + 1);
    return due.toLocaleDateString(localeFmt, { dateStyle: 'medium' });
  };

  /** Ouvre la facture d’impayés en modal — reste sur Gestion des Membres. */
  const openMemberArrearsInvoice = async (member) => {
    const personId = String(member?.id || '').trim();
    if (!personId) return;
    if (String(member?.status || '').toLowerCase() !== 'active') {
      error(t('contributionsPage.invoice.memberInactive'));
      return;
    }
    setInvoiceLoadingMemberId(personId);
    try {
      const contribRes = await membersAPI.getContributions(personId);
      const contributions = normalizeContributionsPayload(contribRes?.data);
      const monthlyAmount = Number(settings?.financial?.contribution_amount || 0);
      const joinMonth = member?.join_date || member?.membership_date || '';
      const targetCount = Number(
        memberStatsById?.[personId]?.unpaid_months ?? memberStatsById?.[personId]?.unpaid_months_harmonized
      );
      const built = buildArrearsInvoiceContributionRows({
        contributions,
        personId,
        monthlyAmount,
        joinMonth,
        monthKeys: listUnpaidRegularMonthKeys(contributions, {
          personId,
          joinMonth,
          maxLookback: 36,
          targetCount: Number.isFinite(targetCount) && targetCount >= 0 ? targetCount : null,
          paymentDeadlineDay: Math.min(
            28,
            Math.max(1, Number(settings?.financial?.contribution_billing?.payment_deadline_day) || 15)
          ),
        }),
      });
      if (!built.length) {
        error(t('contributionsPage.invoice.noArrears'));
        return;
      }
      if (built.every((r) => !(Number(r.amount) > 0))) {
        error(t('contributionsPage.invoice.missingAmount'));
        return;
      }
      setInvoiceData({
        memberName: formatMemberDisplayName(member),
        memberPhone: String(member?.phone || member?.mobile || member?.whatsapp || '').trim(),
        contributions: built.map((c) => ({
          id: c.id,
          month: c.month,
          periodLabel: formatInvoiceMonthLabel(c.month),
          amount: c.amount,
          payment_method: c.payment_method || '',
          status: c.status || 'unpaid',
          statusLabel: t('contributionsPage.invoice.statusUnpaid'),
          notes: c.notes || '',
          person_id: personId,
        })),
        issuedAt: new Date().toLocaleString(localeFmt, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
        dueAt: formatInvoiceDueDate(
          [...built].sort((a, b) => String(a?.month || '').localeCompare(String(b?.month || ''))).at(-1)?.month
        ),
      });
    } catch (_e) {
      error(t('contributionsPage.invoice.loadFailed'));
    } finally {
      setInvoiceLoadingMemberId('');
    }
  };

  const [commissionFormData, setCommissionFormData] = useState({
    name: '',
    description: ''
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingIdentity, setUploadingIdentity] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [iamRoles, setIamRoles] = useState([]);
  const [projectProfileTypes, setProjectProfileTypes] = useState(PROFILE_TYPES);
  const [defaultProfileType, setDefaultProfileType] = useState('member');
  const [selectedIamRole, setSelectedIamRole] = useState('participant');
  const [importingMembers, setImportingMembers] = useState(false);
  const membersImportInputRef = useRef(null);
  const [membersPage, setMembersPage] = useState(1);
  const [membersTotal, setMembersTotal] = useState(0);
  /** Stats cotisation par membre (id People) pour badges retard — page courante uniquement */
  const [memberStatsById, setMemberStatsById] = useState({});
  const [commissionsPage, setCommissionsPage] = useState(1);
  const [commissionsTotal, setCommissionsTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [formData, setFormData] = useState(buildEmptyMemberFormData('member'));
  const nationalityOptions =
    formData.nationality && !NATIONALITY_CODE_SET.has(formData.nationality)
      ? [{ value: formData.nationality, label: formData.nationality }, ...NATIONALITY_OPTIONS]
      : NATIONALITY_OPTIONS;

  /** « Tous » et « Inactifs » nécessitent que le backend renvoie aussi les fiches désactivées. */
  const includeInactive = useMemo(
    () => selectedStatus === 'all' || selectedStatus === 'inactive',
    [selectedStatus]
  );

  useEffect(() => {
    loadMembers();
    loadCommissions();
  }, [membersPage, commissionsPage, itemsPerPage, includeInactive]);

  useEffect(() => {
    if (activeTab !== 'members' || !Array.isArray(members) || members.length === 0) {
      setMemberStatsById({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        members.map(async (m) => {
          try {
            const [statsRes, contribRes] = await Promise.all([
              membersAPI.getStats(m.id),
              membersAPI.getContributions(m.id).catch(() => ({ data: {} })),
            ]);
            const data = statsRes?.data && typeof statsRes.data === 'object' ? statsRes.data : null;
            if (!data) return [m.id, null];
            const contributions = normalizeContributionsPayload(contribRes?.data);
            const personalStats = buildMemberDashboardPersonalStats(
              m.id,
              contributions,
              data,
              formatMemberDisplayName(m),
              String(m.member_number || '').trim()
            );
            // Même liste de mois que Paiements / avis (pas seulement la série traînante)
            const unpaidMonthKeys = listUnpaidRegularMonthKeys(contributions, {
              personId: m.id,
              joinMonth: m.join_date || m.membership_date || m.created_at || '',
              targetCount: Number(data?.unpaid_months) || null,
              maxLookback: 36,
              paymentDeadlineDay: Math.min(
                28,
                Math.max(1, Number(settings?.financial?.contribution_billing?.payment_deadline_day) || 15)
              ),
            });
            const unpaid_months_harmonized = Math.max(
              unpaidMonthKeys.length,
              Number(data?.unpaid_months) || 0,
              harmonizedMemberUnpaidMonthsDisplay(personalStats)
            );
            return [m.id, { ...data, unpaid_months_harmonized, unpaid_month_keys: unpaidMonthKeys }];
          } catch {
            return [m.id, null];
          }
        })
      );
      if (cancelled) return;
      const map = {};
      for (const [mid, data] of entries) {
        if (data && typeof data === 'object') map[mid] = data;
      }
      setMemberStatsById(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [members, activeTab]);

  useEffect(() => {
    loadIamRoles();
    loadProjectProfileTypes();
  }, []);

  const loadProjectProfileTypes = async () => {
    try {
      const res = await peoplePlatformAPI.getProfileTypes();
      const list = Array.isArray(res?.data?.profile_types)
        ? res.data.profile_types
            .map((v) => String(v || '').trim().toLowerCase())
            .filter(Boolean)
        : [];
      const normalized = list.length > 0 ? Array.from(new Set(list)) : PROFILE_TYPES;
      const defaultType = normalized[0] || 'member';
      setProjectProfileTypes(normalized);
      setDefaultProfileType(defaultType);
      setFormData((prev) => (prev?.type ? prev : { ...prev, type: defaultType }));
    } catch (_err) {
      const fallback = PROFILE_TYPES;
      setProjectProfileTypes(fallback);
      setDefaultProfileType(fallback[0] || 'member');
    }
  };


  useEffect(() => {
    let filtered = members;

    // Filtre par commission
    if (selectedCommission === 'simple') {
      filtered = filtered.filter(member => !member.commission);
    } else if (selectedCommission !== 'all') {
      filtered = filtered.filter(member => member.commission === selectedCommission);
    }

    // Filtre par statut
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(member => member.status === selectedStatus);
    }

    // Recherche par nom, email, téléphone ou n° membre (M-00001)
    if (searchQuery.trim()) {
      filtered = filtered.filter((member) =>
        searchFieldsMatch(searchQuery, [
          member.name,
          member.email,
          member.phone,
          member.member_number,
          member.id,
        ])
      );
    }

    setFilteredMembers(filtered);
  }, [selectedCommission, selectedStatus, searchQuery, members]);

  // Ouvrir le modal d'édition si le paramètre edit est présent dans l'URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && members.length > 0) {
      const memberToEdit = members.find(m => m.id === editId);
      if (memberToEdit) {
        setSelectedMember(memberToEdit);
        setFormData({
          name: memberToEdit.name || '',
          email: memberToEdit.email || '',
          phone: memberToEdit.phone || '',
          join_date: memberToEdit.join_date || '',
          status: memberToEdit.status || 'active',
          commission: memberToEdit.commission || '',
          type: memberToEdit.type || 'member',
          first_name: memberToEdit.first_name || '',
          last_name: memberToEdit.last_name || '',
          middle_name: memberToEdit.middle_name || '',
          date_of_birth: memberToEdit.date_of_birth || '',
          place_of_birth: memberToEdit.place_of_birth || '',
          sex: memberToEdit.sex || '',
          marital_status: memberToEdit.marital_status || '',
          nationality: memberToEdit.nationality || '',
          profession: memberToEdit.profession || '',
          id_number: memberToEdit.id_number || '',
          identity_document_type: memberToEdit.identity_document_type || '',
          identity_document_number: memberToEdit.identity_document_number || '',
          identity_document_expiration_date: memberToEdit.identity_document_expiration_date || '',
          identity_document_file_id: memberToEdit.identity_document_file_id || '',
          photo_file_id: memberToEdit.photo_file_id || '',
          address_line1: memberToEdit.address_line1 || '',
          address_line2: memberToEdit.address_line2 || '',
          address_city: memberToEdit.address_city || '',
          address_country: memberToEdit.address_country || '',
          address_postal_code: memberToEdit.address_postal_code || '',
          preferred_language: memberToEdit.preferred_language || '',
          member_number: memberToEdit.member_number || '',
        });
        setOtherDocuments(
          getSupplementaryDocumentIdsFromMemberRow(memberToEdit).map((fid) => ({
            id: fid,
            filename: fid,
          }))
        );
        setShowForm(true);
        // Retirer le paramètre de l'URL
        setSearchParams({});
      }
    }
  }, [searchParams, members, setSearchParams]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (membersPage - 1) * itemsPerPage,
        include_inactive: includeInactive,
      };
      const { list, total } = await memberController.fetchMembersView({
        params,
        currentProjectId,
      });

      setMembers(list);
      setFilteredMembers(list);
      setMembersTotal(total);
    } catch (err) {
      console.error('Failed to load members:', err);
      error('Erreur lors du chargement des membres');
    } finally {
      setLoading(false);
    }
  };

  const loadIamRoles = async () => {
    try {
      setLoadingRoles(true);
      let res;
      try {
        res = await rolesAPI.getList();
      } catch (_e) {
        res = await rolesAPI.getAll();
      }
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw?.roles || []);
      const normalized = list
        .map((item) => (typeof item === 'string' ? item : item?.name || item?.id))
        .filter(Boolean);
      setIamRoles(normalized);
      if (normalized.length > 0 && !normalized.includes(selectedIamRole)) {
        setSelectedIamRole(normalized[0]);
      }
    } catch (_err) {
      setIamRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadCommissions = async () => {
    try {
      const params = {
        limit: itemsPerPage,
        skip: (commissionsPage - 1) * itemsPerPage
      };
      const { rows, total } = await memberController.fetchCommissions(params);
      setCommissions(rows);
      setCommissionsTotal(total);
    } catch (err) {
      console.error('Failed to load commissions:', err);
    }
  };

  const membersTotalPages = Math.max(1, Math.ceil(membersTotal / itemsPerPage));
  const commissionsTotalPages = Math.max(1, Math.ceil(commissionsTotal / itemsPerPage));

  const handleMembersPageSizeChange = (n) => {
    setItemsPerPage(n);
    setMembersPage(1);
    setCommissionsPage(1);
  };

  const renderMembersPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={membersPage}
      totalPages={membersTotalPages}
      totalItems={membersTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setMembersPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={handleMembersPageSizeChange}
    />
  );

  const renderCommissionsPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={commissionsPage}
      totalPages={commissionsTotalPages}
      totalItems={commissionsTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setCommissionsPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={handleMembersPageSizeChange}
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.first_name || '').trim() || !String(formData.last_name || '').trim()) {
      error('Le prenom et le nom sont obligatoires.');
      return;
    }
    if (formData.nationality && !NATIONALITY_CODE_SET.has(formData.nationality)) {
      error('La nationalite doit etre un code ISO alpha-2 (ex: RW, BI, CD).');
      return;
    }
    
    try {
      const personPayload = buildPeopleMemberPayload(
        { ...formData, other_document_file_ids: otherDocuments.map((d) => d.id) },
        currentProjectId
      );
      if (selectedMember) {
        await peoplePlatformAPI.update(selectedMember.id, personPayload);
        if (formData.status === 'inactive') {
          await peoplePlatformAPI.deactivate(selectedMember.id);
        } else {
          await peoplePlatformAPI.reactivate(selectedMember.id);
        }
        success('Membre mis à jour avec succès');
      } else {
        const phoneUser = formData.phone?.replace(/[^\d+]/g, '') || formData.phone?.trim() || '';
        if (!phoneUser) {
          error('Le numéro de téléphone est obligatoire pour créer un membre.');
          return;
        }

        // Pré-vérification: empêcher deux membres avec le même username (téléphone).
        let duplicateExists = false;
        let skipUsers = 0;
        const pageSize = 200;
        while (!duplicateExists) {
          const usersRes = await usersAPI.getAll({ limit: pageSize, skip: skipUsers });
          const rows = usersRes?.data?.users || [];
          duplicateExists = rows.some((u) => {
            const uName = String(u?.username || '').replace(/[^\d+]/g, '').trim();
            return uName && uName === phoneUser;
          });
          const total = Number(usersRes?.data?.total || rows.length);
          skipUsers += rows.length;
          if (duplicateExists || rows.length === 0 || skipUsers >= total) break;
        }
        if (duplicateExists) {
          error('Ce numéro de téléphone est déjà utilisé comme identifiant utilisateur.');
          return;
        }

        const created = await peoplePlatformAPI.create(personPayload);
        const createdId = created?.data?.id;
        if (!createdId) {
          throw new Error('Identifiant membre introuvable après création');
        }

        try {
          await usersAPI.create({
            username: phoneUser,
            member_id: createdId,
            role: selectedIamRole,
            phone: formData.phone,
            email: formData.email || undefined,
            name: [formData.first_name, formData.middle_name, formData.last_name]
              .filter(Boolean)
              .join(' ') || undefined,
            is_active: formData.status !== 'inactive',
          });
        } catch (userErr) {
          // On annule fonctionnellement la création du membre si l'utilisateur ne peut pas être créé.
          try {
            await peoplePlatformAPI.deactivate(createdId);
          } catch (_deactivateErr) {
            // no-op
          }
          throw userErr;
        }

        if (formData.status === 'inactive') {
          await peoplePlatformAPI.deactivate(createdId);
        }

        success(
          'Membre créé avec succès. Un utilisateur a été créé automatiquement avec le numéro de téléphone comme identifiant.'
        );
      }
      
      loadMembers();
      setShowForm(false);
      resetForm();
      // Retirer le paramètre edit de l'URL si présent
      if (searchParams.get('edit')) {
        setSearchParams({});
      }
    } catch (err) {
      console.error('Failed to save member:', err);
      error(getFriendlyContactConflictMessage(err) || err.response?.data?.detail || 'Erreur lors de la sauvegarde du membre');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: 'Désactiver le membre',
      message: 'Êtes-vous sûr de vouloir désactiver ce membre ?',
      confirmText: 'Désactiver',
      cancelText: 'Annuler',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await peoplePlatformAPI.deactivate(id);
      success('Membre désactivé avec succès');
      loadMembers();
    } catch (err) {
      console.error('Failed to delete member:', err);
      error('Erreur lors de la désactivation du membre');
    }
  };

  const handleActivate = async (id) => {
    const confirmed = await confirm({
      title: 'Activer le membre',
      message: 'Êtes-vous sûr de vouloir réactiver ce membre ?',
      confirmText: 'Activer',
      cancelText: 'Annuler',
      type: 'success',
    });

    if (!confirmed) return;

    try {
      await peoplePlatformAPI.reactivate(id);
      success('Membre activé avec succès');
      loadMembers();
    } catch (err) {
      console.error('Failed to activate member:', err);
      error(err?.response?.data?.detail || 'Erreur lors de l’activation du membre');
    }
  };

  const handleHardDelete = async (id) => {
    if (!canHardDeleteMember) {
      error('Seul un admin organisation peut supprimer définitivement un membre.');
      return;
    }
    const confirmed = await confirm({
      title: 'Suppression définitive du membre',
      message: 'Êtes-vous sûr de vouloir supprimer définitivement ce membre ? Cette action est irréversible.',
      confirmText: 'Supprimer définitivement',
      cancelText: 'Annuler',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await membersAPI.hardDelete(id);
      success('Membre supprimé définitivement avec succès');
      loadMembers();
    } catch (err) {
      console.error('Failed to hard delete member:', err);
      error(err?.response?.data?.detail || 'Erreur lors de la suppression définitive du membre');
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = await confirm({
      title: 'Supprimer les membres',
      message: `Êtes-vous sûr de vouloir désactiver ${selectedMembers.length} membre(s) ?`,
      confirmText: 'Désactiver',
      cancelText: 'Annuler',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await Promise.all(selectedMembers.map((id) => peoplePlatformAPI.deactivate(id)));
      success(`${selectedMembers.length} membre(s) désactivé(s) avec succès`);
      setSelectedMembers([]);
      loadMembers();
    } catch (err) {
      console.error('Failed to bulk delete members:', err);
      error('Erreur lors de la désactivation des membres');
    }
  };

  const handleBulkHardDelete = async () => {
    if (!canHardDeleteMember) {
      error('Seul un admin organisation peut supprimer définitivement des membres.');
      return;
    }
    const confirmed = await confirm({
      title: 'Suppression définitive des membres',
      message: `Êtes-vous sûr de vouloir supprimer définitivement ${selectedMembers.length} membre(s) ? Cette action est irréversible.`,
      confirmText: 'Supprimer définitivement',
      cancelText: 'Annuler',
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await membersAPI.bulkHardDelete(selectedMembers);
      success(`${selectedMembers.length} membre(s) supprimé(s) définitivement avec succès`);
      setSelectedMembers([]);
      loadMembers();
    } catch (err) {
      console.error('Failed to bulk hard delete members:', err);
      error(err?.response?.data?.detail || 'Erreur lors de la suppression définitive des membres');
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    try {
      if (status === 'inactive') {
        await Promise.all(selectedMembers.map((id) => peoplePlatformAPI.deactivate(id)));
      } else {
        await Promise.all(selectedMembers.map((id) => peoplePlatformAPI.reactivate(id)));
      }
      success(`Statut mis à jour pour ${selectedMembers.length} membre(s)`);
      setSelectedMembers([]);
      loadMembers();
    } catch (err) {
      console.error('Failed to update status:', err);
      error('Erreur lors de la mise à jour du statut');
    }
  };

  const resetForm = () => {
    setFormData(buildEmptyMemberFormData(defaultProfileType));
    setSelectedMember(null);
    setOtherDocuments([]);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(filteredMembers.map(m => m.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleSelectMember = (id) => {
    setSelectedMembers(prev =>
      prev.includes(id) ? prev.filter(mid => mid !== id) : [...prev, id]
    );
  };

  const handleCreateUsers = async () => {
    if (selectedMembers.length === 0) {
      error('Veuillez sélectionner au moins un membre');
      return;
    }

    try {
      setLoading(true);
      
      // Vérifier quels membres ont déjà un utilisateur
      const membersWithUsers = [];
      const membersWithoutUsers = [];
      
      for (const memberId of selectedMembers) {
        try {
          const userRes = await usersAPI.getByMemberId(memberId);
          if (userRes?.data) {
            membersWithUsers.push(memberId);
          } else {
            membersWithoutUsers.push(memberId);
          }
        } catch (err) {
          // Si l'erreur est 404, le membre n'a pas d'utilisateur
          if (err.response?.status === 404) {
            membersWithoutUsers.push(memberId);
          } else {
            // Autre erreur, on l'ajoute quand même à la liste sans utilisateur
            membersWithoutUsers.push(memberId);
          }
        }
      }

      // Si certains membres ont déjà un utilisateur, demander confirmation
      if (membersWithUsers.length > 0) {
        const memberNames = members
          .filter(m => membersWithUsers.includes(m.id))
          .map(m => m.name)
          .join(', ');
        
        const confirmed = await confirm({
          title: 'Certains membres ont déjà un utilisateur',
          message: `${membersWithUsers.length} membre(s) ont déjà un profil utilisateur (${memberNames}). Voulez-vous créer des utilisateurs uniquement pour les ${membersWithoutUsers.length} autre(s) membre(s) ?`,
          confirmText: 'Créer pour les autres',
          cancelText: 'Annuler',
          type: 'info'
        });
        
        if (!confirmed) {
          setLoading(false);
          return;
        }
      }

      if (membersWithoutUsers.length === 0) {
        error('Tous les membres sélectionnés ont déjà un profil utilisateur');
        setLoading(false);
        return;
      }

      // Créer les utilisateurs pour les membres sans utilisateur
      const membersToCreate = members.filter(m => membersWithoutUsers.includes(m.id));
      let createdCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const member of membersToCreate) {
        try {
          // Vérifier que le membre a un téléphone (requis pour créer un utilisateur)
          if (!member.phone) {
            throw new Error('Le membre doit avoir un numéro de téléphone');
          }
          
          // Le backend va générer automatiquement le password, token de validation, PIN
          // username doit être fourni (on utilise le téléphone)
          await usersAPI.create({
            username: member.phone, // Utiliser le téléphone comme username
            role: selectedIamRole,
            member_id: member.id,
            phone: member.phone,
            email: member.email || undefined, // Ne pas envoyer si vide
            name: member.name || undefined // Ne pas envoyer si vide
          });
          createdCount++;
        } catch (err) {
          errorCount++;
          let errorMessage = 'Erreur inconnue';
          const conflictMessage = getFriendlyContactConflictMessage(err);
          if (conflictMessage) {
            errorMessage = conflictMessage;
          } else
          if (err.response?.data?.detail) {
            if (typeof err.response.data.detail === 'string') {
              errorMessage = err.response.data.detail;
            } else if (Array.isArray(err.response.data.detail)) {
              errorMessage = err.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ');
            } else {
              errorMessage = JSON.stringify(err.response.data.detail);
            }
          } else if (err.message) {
            errorMessage = err.message;
          }
          errors.push(`${member.name}: ${errorMessage}`);
        }
      }

      if (createdCount > 0) {
        success(`${createdCount} utilisateur(s) créé(s) avec succès pour les membres sélectionnés`);
        setSelectedMembers([]);
        // Optionnel : rediriger vers la page des utilisateurs ou recharger les membres
      }
      
      if (errorCount > 0) {
        error(`${errorCount} erreur(s) lors de la création. Détails: ${errors.join('; ')}`);
      }
    } catch (err) {
      console.error('Failed to create users:', err);
      error('Erreur lors de la création des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleImportMembersFile = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setImportingMembers(true);
      const rows = await parseTabularFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        error('Le fichier est vide ou invalide');
        return;
      }
      const { data } = await adminAPI.importData('members', rows, 'json');
      const created = Number(data?.created_count || 0);
      const usersCreated = Number(data?.users_created || 0);
      const usersLinked = Number(data?.users_linked || 0);
      const errorsCount = Array.isArray(data?.errors) ? data.errors.length : 0;
      if (errorsCount > 0) {
        const firstError = data.errors[0]?.error || 'Erreur non détaillée';
        success(
          `Import terminé : ${created} membre(s), ${usersCreated} compte(s) créé(s), ${usersLinked} compte(s) lié(s), ${errorsCount} erreur(s) (première : ${firstError}).`
        );
      } else {
        success(
          `Import terminé : ${created} membre(s), ${usersCreated} compte(s) créé(s), ${usersLinked} compte(s) lié(s).`
        );
      }
      await loadMembers();
    } catch (err) {
      error(err?.response?.data?.detail || 'Erreur lors de l import des membres');
    } finally {
      setImportingMembers(false);
    }
  };

  const uploadFileAndSetField = async (file, fieldName, label) => {
    if (!file) return;
    const setUploading =
      fieldName === 'photo_file_id' ? setUploadingPhoto : setUploadingIdentity;
    try {
      setUploading(true);
      const uploaded = await filesPlatformAPI.upload(file);
      const fileId = uploaded?.data?.id;
      if (!fileId) throw new Error('ID fichier manquant');
      setFormData((prev) => ({ ...prev, [fieldName]: String(fileId) }));
      success(`${label} téléversé avec succès`);
    } catch (err) {
      error(
        err?.response?.data?.detail ||
          err?.message ||
          `Erreur lors du téléversement ${label.toLowerCase()}`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCommissionSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedCommissionItem) {
        await memberController.updateCommission(selectedCommissionItem.id, commissionFormData);
        success('Commission mise à jour avec succès');
      } else {
        await memberController.createCommission(commissionFormData);
        success('Commission créée avec succès');
      }
      
      loadCommissions();
      setShowCommissionForm(false);
      setCommissionFormData({ name: '', description: '' });
      setSelectedCommissionItem(null);
    } catch (err) {
      console.error('Failed to save commission:', err);
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde de la commission');
    }
  };

  const handleCommissionDelete = async (id) => {
    const commission = commissions.find(c => c.id === id);
    const confirmed = await confirm({
      title: 'Supprimer la commission',
      message: `Êtes-vous sûr de vouloir supprimer la commission "${commission?.name}" ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await memberController.deleteCommission(id);
      success('Commission supprimée avec succès');
      loadCommissions();
    } catch (err) {
      console.error('Failed to delete commission:', err);
      error(err.response?.data?.detail || 'Erreur lors de la suppression de la commission');
    }
  };

  const handleCommissionEdit = (commission) => {
    setSelectedCommissionItem(commission);
    setCommissionFormData({
      name: commission.name || '',
      description: commission.description || ''
    });
    setShowCommissionForm(true);
  };

  const resetCommissionForm = () => {
    setCommissionFormData({ name: '', description: '' });
    setSelectedCommissionItem(null);
  };

      const canManageCommissions = hasPermission('commissions.edit') || hasPermission('members.edit');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <PageHeader
        title="Gestion des Membres"
        subtitle="Gérez les membres et commissions de votre association"
        actions={
          <>
            {activeTab === 'members' && hasPermission('members.create') && (
              <div className="flex items-center gap-2">
                <input
                  ref={membersImportInputRef}
                  type="file"
                  accept=".csv,.txt,.xls,.xlsx"
                  className="hidden"
                  onChange={handleImportMembersFile}
                />
                <UiSurface id="members.import">
                  <button
                    type="button"
                    onClick={() => membersImportInputRef.current?.click()}
                    disabled={importingMembers}
                    className="px-4 py-2 border border-entrea-royal text-entrea-royal rounded-lg hover:bg-entrea-sky/10 transition font-medium text-sm whitespace-nowrap disabled:opacity-60"
                  >
                    {importingMembers ? 'Import en cours...' : 'Importer membres'}
                  </button>
                </UiSurface>
                <UiSurface id="members.import_template">
                  <button
                    type="button"
                    onClick={downloadMembersImportTemplate}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm whitespace-nowrap"
                  >
                    Modèle CSV
                  </button>
                </UiSurface>
                <button
                  onClick={() => {
                    resetForm();
                    setShowForm(true);
                  }}
                  className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap"
                >
                  + Ajouter un membre
                </button>
              </div>
            )}
            {activeTab === 'commissions' && canManageCommissions && (
              <button
                onClick={() => {
                  resetCommissionForm();
                  setShowCommissionForm(true);
                }}
                className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap"
              >
                + Ajouter une commission
              </button>
            )}
          </>
        }
      />

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'members'
                ? 'bg-entrea-royal text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Membres
          </button>
          <button
            onClick={() => setActiveTab('commissions')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
              activeTab === 'commissions'
                ? 'bg-entrea-royal text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            Commissions
          </button>
        </div>
      </div>

      {/* Members Tab Content */}
      {activeTab === 'members' && (
        <>
      {/* Barre de recherche et filtres */}
      <SearchAndFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Rechercher par nom, email, téléphone ou n° membre (M-00001)..."
        collapseSearchOnMobile
        mobileFiltersToggleLabel="Filtres"
        filters={[
          {
            key: 'commission',
            label: 'Groupe / commission',
            type: 'select',
            value: selectedCommission,
            onChange: setSelectedCommission,
            options: [
              { value: 'all', label: 'Toutes les commissions' },
              { value: 'simple', label: 'Simples membres' },
              ...commissions
                .filter((c) => c && c.name)
                .map((c) => ({ value: c.name, label: c.name }))
            ]
          },
          {
            key: 'status',
            label: 'Statut',
            type: 'button-group',
            value: selectedStatus,
            onChange: setSelectedStatus,
            options: [
              { value: 'all', label: 'Tous' },
              { value: 'active', label: 'Actifs' },
              { value: 'inactive', label: 'Inactifs' }
            ]
          }
        ]}
      />

      {selectedMembers.length > 0 &&
        !isMobileSurface &&
        (hasPermission('members.bulk_operations') ||
          hasPermission('members.delete') ||
          hasPermission('users.create')) && (
        <UiSurface id="members.bulk_bar">
        <div className="bg-entrea-panel rounded-lg shadow-sm p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-entrea-sky/40">
          <span className="text-sm text-gray-900 font-semibold flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-entrea-royal text-white flex items-center justify-center text-xs font-bold">
              {selectedMembers.length}
            </div>
            membre(s) sélectionné(s)
          </span>
          <div className="flex flex-wrap gap-2">
            {hasPermission('users.create') && (
              <select
                value={selectedIamRole}
                onChange={(e) => setSelectedIamRole(e.target.value)}
                className="px-3 py-1.5 text-xs sm:text-sm border border-entrea-sky/40 rounded-lg bg-white"
                disabled={loadingRoles || iamRoles.length === 0}
                title="Rôle IAM à assigner aux utilisateurs créés"
              >
                {iamRoles.length === 0 ? (
                  <option value="participant">participant</option>
                ) : (
                  iamRoles.map((r, idx) => (
                    <option key={`iam-role-${idx}-${String(r)}`} value={r}>
                      {r}
                    </option>
                  ))
                )}
              </select>
            )}
            {hasPermission('users.create') && (
              <button
                onClick={handleCreateUsers}
                disabled={loading}
                className="px-3 py-1.5 text-xs sm:text-sm text-white bg-entrea-royal rounded-lg hover:bg-entrea-navy transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Création...' : 'Créer utilisateur'}
              </button>
            )}
            {hasPermission('members.bulk_operations') && (
              <>
            <button
              onClick={() => handleBulkStatusUpdate('active')}
              className="px-3 py-1.5 text-xs sm:text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 transition font-medium"
            >
              Activer
            </button>
            <button
              onClick={() => handleBulkStatusUpdate('inactive')}
              className="px-3 py-1.5 text-xs sm:text-sm text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition font-medium"
            >
              Désactiver
            </button>
              </>
            )}
            {hasPermission('members.delete') && (
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-xs sm:text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition font-medium"
            >
              Désactiver
            </button>
            )}
            {hasPermission('members.delete') && canHardDeleteMember && (
            <button
              type="button"
              onClick={handleBulkHardDelete}
              title="Supprimer définitivement"
              aria-label="Supprimer définitivement"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-red-800 text-white hover:bg-red-900 transition"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                <path
                  fillRule="evenodd"
                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            )}
          </div>
        </div>
        </UiSurface>
      )}

      {/* Mobile Cards View - Only visible on mobile */}
      <div className="lg:hidden space-y-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {renderMembersPagination('top')}
        <div className="space-y-3 px-1">
        {filteredMembers.map((member) => {
          const stats = memberStatsById[member.id];
          const lastPaidLabel =
            stats?.last_regular_month_paid != null &&
            String(stats.last_regular_month_paid).trim().length >= 7
              ? formatLongMonthYearCapitalized(stats.last_regular_month_paid, localeFmt)
              : null;
          const unpaidMonthsActive =
            member.status === 'active' && stats != null ? unpaidMonthsDisplayFromStats(stats) : 0;
          const showArrearsStrip = unpaidMonthsActive > 0;
          const arrearsCriticalMobile = showArrearsStrip && isArrearsCritical(unpaidMonthsActive);

          return (
          <div
            key={member.id}
            className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start gap-3">
              {!isMobileSurface ? (
                <TableListCheckbox
                  checked={selectedMembers.includes(member.id)}
                  onChange={() => handleSelectMember(member.id)}
                  size="lg"
                  aria-label={`Sélectionner ${formatMemberDisplayName(member)}`}
                />
              ) : null}
              <div className="h-12 w-12 shrink-0 rounded-full bg-[#113a5b] flex items-center justify-center">
                <span className="text-base font-bold text-white">
                  {formatMemberDisplayName(member).charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                {lastPaidLabel ? (
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#113a5b]/90">
                    {t('memberDashboard.lastPaidMonthLine', { month: lastPaidLabel })}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate(`${membersBasePath}/${member.id}`)}
                  className="block w-full truncate text-left text-base font-bold text-gray-900 hover:text-[#113a5b]"
                >
                  {formatMemberDisplayName(member)}
                </button>
                <p className="truncate text-xs text-gray-500">{member.email}</p>
                {member.member_number ? (
                  <p className="mt-0.5 text-xs font-semibold font-mono tracking-wide text-[#113a5b]">
                    N° {member.member_number}
                  </p>
                ) : null}
              </div>
              <div className="shrink-0 pt-0.5">
                <span
                  className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                    member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}
                >
                  {member.status === 'active' ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>

            {showArrearsStrip ? (
              <div
                className={`mt-3 rounded-xl border px-3 py-2.5 ${
                  arrearsCriticalMobile
                    ? 'border-red-200/90 bg-red-50/95 text-red-950'
                    : 'border-amber-200/90 bg-amber-50/95 text-amber-950'
                }`}
              >
                <p className="text-xs font-bold leading-snug">{t('member.unpaidMonthsBadge', { count: unpaidMonthsActive })}</p>
                {arrearsCriticalMobile ? (
                  <p className="mt-1 text-[11px] font-semibold leading-snug text-red-900/95">
                    {t('member.arrearsDeactivateHint', { months: CONTRIBUTION_ARREARS_CRITICAL_MONTHS })}
                  </p>
                ) : null}
              </div>
            ) : null}

            <UiSurface id="members.card_details">
            <div className="mt-3 grid grid-cols-2 gap-2.5 border-t border-slate-100 pt-3">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                <p className="text-sm font-semibold text-gray-900">{member.phone}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Position</p>
                <p className="text-sm font-semibold text-gray-900">{memberProjectPositionLabel(member)}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500 mb-0.5">Groupe / commission</p>
                <p className="text-sm font-semibold text-gray-900">
                  {member.commission?.trim() ? member.commission : '—'}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-500 mb-0.5">Date d'adhésion</p>
                <p className="text-sm font-semibold text-gray-900">{member.join_date}</p>
              </div>
            </div>
            </UiSurface>

            {(hasPermission('members.edit') ||
              hasPermission('members.delete') ||
              showArrearsStrip) && (
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-3">
                {showArrearsStrip ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMemberArrearsInvoice(member);
                    }}
                    disabled={invoiceLoadingMemberId === member.id}
                    title={t('member.viewInvoice')}
                    aria-label={t('member.viewInvoiceAria', {
                      name: formatMemberDisplayName(member),
                    })}
                    className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-amber-300/80 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-950 transition hover:bg-amber-100 disabled:opacity-60"
                  >
                    <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    {invoiceLoadingMemberId === member.id ? '…' : t('member.viewInvoice')}
                  </button>
                ) : null}
                <TableRowActions
                  onEdit={
                    hasPermission('members.edit') ? () => navigate(`${membersBasePath}/${member.id}/edit`) : undefined
                  }
                  onActivate={
                    hasPermission('members.delete') && String(member.status || '').toLowerCase() !== 'active'
                      ? () => handleActivate(member.id)
                      : undefined
                  }
                  onDeactivate={
                    hasPermission('members.delete') && String(member.status || '').toLowerCase() === 'active'
                      ? () => handleDelete(member.id)
                      : undefined
                  }
                  onHardDelete={
                    hasPermission('members.delete') && canHardDeleteMember
                      ? () => handleHardDelete(member.id)
                      : undefined
                  }
                />
              </div>
            )}
          </div>
          );
        })}
        {filteredMembers.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">Aucun membre trouvé</p>
          </div>
        )}
        </div>
        {renderMembersPagination('bottom')}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        {renderMembersPagination('top')}
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left">
                <TableListCheckbox
                  onChange={handleSelectAll}
                  checked={
                    selectedMembers.length === filteredMembers.length && filteredMembers.length > 0
                  }
                  aria-label="Tout sélectionner sur la page"
                />
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Membre
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Position
              </th>
              <th
                className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                title="Groupe IAM Entrea rattaché au membre (People : métadonnées groups)"
              >
                Groupe / commission
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date d'adhésion
              </th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statut
              </th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredMembers.map((member) => {
              const ms = memberStatsById[member.id];
              const unpaidHarm = unpaidMonthsDisplayFromStats(ms);
              return (
              <tr key={member.id} className="hover:bg-gray-50 transition">
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <TableListCheckbox
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => handleSelectMember(member.id)}
                      aria-label={`Sélectionner ${formatMemberDisplayName(member)}`}
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer whitespace-normal md:max-w-[18rem]"
                  onClick={(e) => {
                    // Ne pas naviguer si on clique sur un bouton ou checkbox
                    if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'INPUT') {
                      navigate(`${membersBasePath}/${member.id}`);
                    }
                  }}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-[#113a5b] flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {formatMemberDisplayName(member).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4 min-w-0">
                      <div className="text-sm font-medium text-gray-900 hover:text-[#113a5b]">
                        {formatMemberDisplayName(member)}
                      </div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                      {member.member_number ? (
                        <div className="text-xs font-semibold text-[#113a5b] font-mono mt-0.5">
                          N° {member.member_number}
                        </div>
                      ) : null}
                      {(() => {
                        const lp = ms?.last_regular_month_paid;
                        const label = lp ? formatLongMonthYearCapitalized(lp, localeFmt) : null;
                        return label ? (
                          <div className="mt-1 max-w-[16rem] whitespace-normal text-[11px] font-semibold uppercase tracking-wide text-[#113a5b]/90">
                            {t('memberDashboard.lastPaidMonthLine', { month: label })}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.phone}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{memberProjectPositionLabel(member)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {member.commission?.trim() ? member.commission : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{member.join_date}</td>
                <td className="px-4 sm:px-6 py-4 align-top">
                  <div className="flex min-w-[8.5rem] flex-col gap-2">
                    <span
                      className={`inline-flex w-fit px-3 py-1 text-xs leading-5 font-semibold rounded-full ${
                        member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {member.status === 'active' ? 'Actif' : 'Inactif'}
                    </span>
                    {member.status === 'active' && ms != null && unpaidHarm > 0 ? (
                      <div
                        className={`max-w-[14rem] rounded-lg border px-2.5 py-1.5 ${
                          isArrearsCritical(unpaidHarm)
                            ? 'border-red-200/90 bg-red-50 text-red-950'
                            : 'border-amber-200/90 bg-amber-50 text-amber-950'
                        }`}
                      >
                        <p className="text-xs font-bold leading-snug">
                          {t('member.unpaidMonthsBadge', { count: unpaidHarm })}
                        </p>
                        {isArrearsCritical(unpaidHarm) ? (
                          <p className="mt-1 text-[11px] font-semibold leading-snug text-red-900/95">
                            {t('member.arrearsDeactivateHint', {
                              months: CONTRIBUTION_ARREARS_CRITICAL_MONTHS,
                            })}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    {member.status === 'active' && ms != null && unpaidHarm > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMemberArrearsInvoice(member);
                        }}
                        disabled={invoiceLoadingMemberId === member.id}
                        title={t('member.viewInvoice')}
                        aria-label={t('member.viewInvoiceAria', {
                          name: formatMemberDisplayName(member),
                        })}
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-amber-800 hover:text-amber-950 hover:bg-amber-50 transition disabled:opacity-60"
                      >
                        {invoiceLoadingMemberId === member.id ? (
                          <span className="text-xs font-bold">…</span>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        )}
                      </button>
                    ) : null}
                    <TableRowActions
                      onEdit={
                        hasPermission('members.edit')
                          ? () => navigate(`${membersBasePath}/${member.id}/edit`)
                          : undefined
                      }
                      onActivate={
                        hasPermission('members.delete') && String(member.status || '').toLowerCase() !== 'active'
                          ? () => handleActivate(member.id)
                          : undefined
                      }
                      onDeactivate={
                        hasPermission('members.delete') && String(member.status || '').toLowerCase() === 'active'
                          ? () => handleDelete(member.id)
                          : undefined
                      }
                      onHardDelete={
                        hasPermission('members.delete') && canHardDeleteMember
                          ? () => handleHardDelete(member.id)
                          : undefined
                      }
                    />
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        {renderMembersPagination('bottom')}
      </div>

      {/* Modal Form */}
      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
          panelClassName="border-gray-200 p-0 sm:border"
        >
          <div className="relative bg-gradient-to-br from-white via-entrea-panel/45 to-entrea-surface/40 p-4 sm:p-6 md:bg-white">
            {/* Decorative background */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-entrea-sky/25 to-entrea-royal/20 rounded-full -mr-20 -mt-20 blur-3xl md:hidden"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-br from-entrea-sky/25 to-entrea-royal/20 rounded-full -ml-16 -mb-16 blur-2xl md:hidden"></div>
            
            <div className="relative flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-entrea-ink to-entrea-royal bg-clip-text text-transparent md:text-gray-900">
                {selectedMember ? 'Modifier le membre' : 'Nouveau membre'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-500 active:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-full hover:bg-white md:bg-transparent md:p-2 md:rounded-lg md:hover:bg-gray-100 transition"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prenom *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                    placeholder="Entrez le prenom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                    placeholder="Entrez le nom"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Post-nom</label>
                <input
                  type="text"
                  value={formData.middle_name}
                  onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  placeholder="Optionnel"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                    placeholder="Entrez l'email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                    placeholder="Entrez le numéro"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de profil</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {projectProfileTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Type par defaut du projet: <span className="font-semibold">{defaultProfileType}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom complet (genere)</label>
                  <input
                    type="text"
                    value={[formData.first_name, formData.middle_name, formData.last_name].filter(Boolean).join(' ')}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-200 bg-gray-50 rounded-lg text-gray-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de naissance</label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lieu de naissance</label>
                  <input
                    type="text"
                    value={formData.place_of_birth}
                    onChange={(e) => setFormData({ ...formData, place_of_birth: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sexe</label>
                  <select
                    value={formData.sex}
                    onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {SEX_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Etat civil</label>
                  <select
                    value={formData.marital_status}
                    onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {MARITAL_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nationalité</label>
                  <select
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {nationalityOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Profession</label>
                  <input
                    type="text"
                    value={formData.profession}
                    onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
              </div>

              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Groupe / commission</label>
                  <select
                    value={formData.commission || ''}
                    onChange={(e) => setFormData({ ...formData, commission: e.target.value || '' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    <option value="">Aucun groupe</option>
                    {commissions.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date d'adhésion</label>
                  <input
                    type="date"
                    value={formData.join_date}
                    onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    required
                  >
                    <option value="active">Actif</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">N° document d'identité</label>
                  <input
                    type="text"
                    value={formData.identity_document_number}
                    onChange={(e) =>
                      setFormData({ ...formData, identity_document_number: e.target.value, id_number: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type document</label>
                  <select
                    value={formData.identity_document_type}
                    onChange={(e) => setFormData({ ...formData, identity_document_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {IDENTITY_DOC_TYPES.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiration document</label>
                  <input
                    type="date"
                    value={formData.identity_document_expiration_date}
                    onChange={(e) => setFormData({ ...formData, identity_document_expiration_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Photo</span>
                  <DocumentFileDropZone
                    variant="photo"
                    disabled={loading}
                    uploading={uploadingPhoto}
                    title="Portrait du membre"
                    hint="Formats image courants."
                    onFileSelected={(file) => uploadFileAndSetField(file, 'photo_file_id', 'Photo')}
                    onInvalidFile={(msg) => error(msg)}
                  />
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-700 mb-2">Pièce d&apos;identité</span>
                  <DocumentFileDropZone
                    variant="identity"
                    disabled={loading}
                    uploading={uploadingIdentity}
                    title="Scan ou photo de la pièce"
                    hint="Image ou PDF."
                    onFileSelected={(file) =>
                      uploadFileAndSetField(file, 'identity_document_file_id', 'Pièce')
                    }
                    onInvalidFile={(msg) => error(msg)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse ligne 1</label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                  <input
                    type="text"
                    value={formData.address_city}
                    onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                  <input
                    type="text"
                    value={formData.address_country}
                    onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code postal</label>
                  <input
                    type="text"
                    value={formData.address_postal_code}
                    onChange={(e) => setFormData({ ...formData, address_postal_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Langue préférée</label>
                  <select
                    value={formData.preferred_language}
                    onChange={(e) => setFormData({ ...formData, preferred_language: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <SupplementaryDocumentsUploader
                documents={otherDocuments}
                onDocumentsChange={setOtherDocuments}
                disabled={loading}
              />

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm"
                >
                  {selectedMember ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </ModalShell>
      )}
        </>
      )}

      {/* Commissions Tab Content */}
      {activeTab === 'commissions' && (
        <>
          {/* Barre de recherche pour commissions */}
          <SearchAndFilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            placeholder="Rechercher par nom ou description..."
            filters={[]}
          />

          {/* Liste des commissions - Mobile Cards */}
          <div className="lg:hidden rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {renderCommissionsPagination('top')}
            <div className="grid grid-cols-1 gap-3 p-1">
            {commissions.filter(c => {
              if (!searchQuery.trim()) return true;
              return searchFieldsMatch(searchQuery, [c.name, c.description]);
            }).map((commission) => (
              <div key={commission.id} className="relative bg-white rounded-2xl shadow-xl p-5 border-0 hover:shadow-2xl transition-all overflow-hidden">
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-2xl shadow-lg shadow-entrea-royal/25">
                      <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    {canManageCommissions && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCommissionEdit(commission)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                          title="Modifier"
                        >
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleCommissionDelete(commission.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Supprimer"
                        >
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{commission.name}</h3>
                  {commission.description && (
                    <p className="text-xs sm:text-sm text-gray-600 mb-4 line-clamp-3">{commission.description}</p>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Créée le {new Date(commission.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
            ))}
            </div>
            {renderCommissionsPagination('bottom')}
          </div>

          {/* Desktop Table View for Commissions */}
          <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
            {renderCommissionsPagination('top')}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date de création
                    </th>
                    {canManageCommissions && (
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissions.filter(c => {
                    if (!searchQuery.trim()) return true;
                    return searchFieldsMatch(searchQuery, [c.name, c.description]);
                  }).map((commission) => (
                    <tr key={commission.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{commission.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{commission.description || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(commission.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      {canManageCommissions && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <TableRowActions
                            onEdit={() => handleCommissionEdit(commission)}
                            onDelete={() => handleCommissionDelete(commission.id)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {renderCommissionsPagination('bottom')}
          </div>

          {commissions.length === 0 && (
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="mt-4 text-gray-900 font-medium">Aucune commission</p>
              <p className="mt-2 text-sm text-gray-500">Commencez par créer votre première commission</p>
              {canManageCommissions && (
                <button
                  onClick={() => {
                    resetCommissionForm();
                    setShowCommissionForm(true);
                  }}
                  className="mt-6 px-6 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium"
                >
                  + Créer une commission
                </button>
              )}
            </div>
          )}

          {/* Modal de création/édition de commission */}
          {showCommissionForm && (
            <ModalShell
              maxWidthClass="max-w-md"
              onBackdropClick={() => {
                setShowCommissionForm(false);
                resetCommissionForm();
              }}
            >
              <div className="p-4 sm:p-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                    {selectedCommissionItem ? 'Modifier la commission' : 'Nouvelle commission'}
                  </h2>
                  <form onSubmit={handleCommissionSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom de la commission *
                      </label>
                      <input
                        type="text"
                        required
                        value={commissionFormData.name}
                        onChange={(e) => setCommissionFormData({ ...commissionFormData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                        placeholder="Ex: Commission de Suivi et Adhésion"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (optionnel)
                      </label>
                      <textarea
                        value={commissionFormData.description}
                        onChange={(e) => setCommissionFormData({ ...commissionFormData, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                        rows="4"
                        placeholder="Description de la commission..."
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowCommissionForm(false);
                          resetCommissionForm();
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm"
                      >
                        {selectedCommissionItem ? 'Modifier' : 'Créer'}
                      </button>
                    </div>
                  </form>
              </div>
            </ModalShell>
          )}
        </>
      )}

      <ContributionInvoiceModal
        open={!!invoiceData}
        onClose={() => setInvoiceData(null)}
        settings={settings}
        orgName={getResolvedPortalTitle(settings)}
        logoUrl={String(settings?.organization?.logo_url || '').trim()}
        memberName={invoiceData?.memberName || ''}
        memberPhone={invoiceData?.memberPhone || ''}
        contributions={invoiceData?.contributions || []}
        issuedAt={invoiceData?.issuedAt || ''}
        dueAt={invoiceData?.dueAt || ''}
        localeFmt={localeFmt}
        onToast={(kind, message) => {
          if (kind === 'error') error(message);
          else success(message);
        }}
      />

    </div>
  );
};

export default Members;
